import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Col, Row, Skeleton, Space, Statistic} from 'antd';
import {
    AppstoreOutlined,
    ArrowRightOutlined,
    BookOutlined,
    FileTextOutlined,
    FolderOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {getTermDataList} from '../api/termDataApi';
import {getWordDataList} from '../api/wordDataApi';
import {getDomainDataList} from '../api/domainDataApi';
import {getProjectList} from '../api/projectApi';
import useApiPermission from '../hooks/useApiPermission';

export default function HomePage() {
  const [counts, setCounts] = useState({ terms: null, words: null, domains: null, projects: null });
  const [loading, setLoading] = useState(false);
  // 원시 데이터 저장하여 집계에 활용
  const [datasets, setDatasets] = useState({ terms: [], words: [], domains: [], projects: [] });

  const { canCall, loading: permLoading } = useApiPermission();
  const allow = useMemo(() => ({
    terms: canCall(getTermDataList),
    words: canCall(getWordDataList),
    domains: canCall(getDomainDataList),
    projects: canCall(getProjectList),
  }), [canCall]);

  // 분포 유틸
  const topCounts = useCallback((arr, field, topN = 5) => {
    if (!Array.isArray(arr) || !field) return [];
    const map = new Map();
    for (const it of arr) {
      const k = (it?.[field] ?? '').toString() || '(없음)';
      map.set(k, (map.get(k) || 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a,b) => b[1]-a[1])
      .slice(0, topN)
      .map(([name, value]) => ({ name, value }));
  }, []);

  // 최근 7일 내 아이템만 필터링 후 최신순 정렬
  const recentBy = useCallback((arr, nameField, dateField = 'regDt', limit = 10) => {
    if (!Array.isArray(arr)) return [];
    const now = new Date();
    const since = new Date(now);
    since.setDate(now.getDate() - 6); // 오늘 포함 7일 창(오늘~6일 전)
    since.setHours(0,0,0,0);
    const items = arr.filter(it => {
      const v = it?.[dateField];
      if (!v) return false;
      const d = new Date(v);
      if (isNaN(d.getTime())) return false;
      return d >= since && d <= now;
    }).map(it => ({ name: it?.[nameField] || '-', date: it?.[dateField] }));
    return items.sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0, limit);
  }, []);

  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const y=d.getFullYear(); const m=String(d.getMonth()+1).padStart(2,'0'); const da=String(d.getDate()).padStart(2,'0');
    const hh=String(d.getHours()).padStart(2,'0'); const mm=String(d.getMinutes()).padStart(2,'0');
    return `${y}-${m}-${da} ${hh}:${mm}`;
  };

  const fetchCounts = useCallback(async () => {
    if (permLoading) return;
    setLoading(true);
    try {
      const res = await Promise.allSettled([
        allow.terms ? getTermDataList() : Promise.resolve(null),
        allow.words ? getWordDataList() : Promise.resolve(null),
        allow.domains ? getDomainDataList() : Promise.resolve(null),
        allow.projects ? getProjectList() : Promise.resolve(null),
      ]);
      const [t, w, d, p] = res;
      const safeArr = (r) => (r && r.status === 'fulfilled' && r.value && Array.isArray(r.value.data)) ? r.value.data : [];
      const terms = safeArr(t);
      const words = safeArr(w);
      const domains = safeArr(d);
      const projects = safeArr(p);
      setCounts({
        terms: terms.length || null,
        words: words.length || null,
        domains: domains.length || null,
        projects: projects.length || null,
      });
      setDatasets({ terms, words, domains, projects });
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally {
      setLoading(false);
    }
  }, [permLoading, allow.terms, allow.words, allow.domains, allow.projects]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const cards = [
    { key: 'terms', title: '용어', icon: <BookOutlined style={{ color: '#1890ff' }} />, to: '/terms', value: counts.terms, allowed: allow.terms },
    { key: 'words', title: '단어', icon: <FileTextOutlined style={{ color: '#52c41a' }} />, to: '/words', value: counts.words, allowed: allow.words },
    { key: 'domains', title: '도메인', icon: <AppstoreOutlined style={{ color: '#faad14' }} />, to: '/domains', value: counts.domains, allowed: allow.domains },
    { key: 'projects', title: '프로젝트', icon: <FolderOutlined style={{ color: '#722ed1' }} />, to: '/projects', value: counts.projects, allowed: allow.projects },
  ];

  // 분포 및 추이 데이터(기존 분포 위젯 유지)
  const termDomainDist = useMemo(() => allow.terms ? topCounts(datasets.terms, 'domainNm') : [], [allow.terms, datasets.terms, topCounts]);
  const wordDomainDist = useMemo(() => allow.words ? topCounts(datasets.words, 'domainClsfNm') : [], [allow.words, datasets.words, topCounts]);
  const domainTypeDist = useMemo(() => allow.domains ? topCounts(datasets.domains, 'dataType') : [], [allow.domains, datasets.domains, topCounts]);

  // 최근 7일 명칭 리스트 계산
  const recentTerms = useMemo(() => allow.terms ? recentBy(datasets.terms, 'trmdNm', 'regDt', 10) : [], [allow.terms, datasets.terms, recentBy]);
  const recentWords = useMemo(() => allow.words ? recentBy(datasets.words, 'wordNm', 'regDt', 10) : [], [allow.words, datasets.words, recentBy]);
  const recentDomains = useMemo(() => allow.domains ? recentBy(datasets.domains, 'domainNm', 'regDt', 10) : [], [allow.domains, datasets.domains, recentBy]);
  const recentProjects = useMemo(() => allow.projects ? recentBy(datasets.projects, 'pjtNm', 'regDt', 10) : [], [allow.projects, datasets.projects, recentBy]);

  // 미니 차트 UI (분포 표시용 바)
  const MiniBar = ({ data = [], color = '#91d5ff' }) => {
    if (!data.length) return null;
    const max = Math.max(...data.map(d => d.value), 1);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.map(d => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ background: '#f5f5f5', height: 10, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${(d.value / max) * 100}%`, height: '100%', background: color }} />
              </div>
            </div>
            <div style={{ minWidth: 48, textAlign: 'right', fontSize: 12, color: '#595959' }}>{d.value}</div>
          </div>
        ))}
      </div>
    );
  };

  // 최근 7일 명칭 리스트 UI
  const RecentList = ({ items = [], emptyText = '최근 7일 등록 없음' }) => (
    <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight: 180, overflowY: 'auto' }}>
      {items.length ? items.map((it, idx) => (
        <div key={`${it.name}-${it.date}-${idx}`} style={{ display:'flex', justifyContent:'space-between', gap:12 }}>
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.name}</span>
          <span style={{ color:'#8c8c8c', flexShrink:0 }}>{fmtDate(it.date)}</span>
        </div>
      )) : (
        <span style={{ color:'#8c8c8c' }}>{emptyText}</span>
      )}
    </div>
  );

  return (
    <div style={{ padding: 8 }}>
      {/* KPI 카드 */}
      <Row gutter={[16, 16]}>
        {cards.map((c) => (
          <Col xs={24} sm={12} md={12} lg={6} key={c.key}>
            <Card
              hoverable
              style={{ borderRadius: 12, height: 160 }}
              styles={{ body: { height: '100%' } }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Space align="center" size={12} style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>{c.icon}</span>
                  <span style={{ fontWeight: 600 }}>{c.title}</span>
                </Space>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  {loading || permLoading ? (
                    <Skeleton active paragraph={false} title={{ width: 80 }} />
                  ) : (
                    <Statistic value={typeof c.value === 'number' ? c.value : (c.allowed ? '-' : '권한없음')} suffix="개" valueStyle={{ fontSize: 24 }} />
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {c.allowed ? (
                    <Link to={c.to}>
                      <Button type="link" icon={<ArrowRightOutlined />}>바로가기</Button>
                    </Link>
                  ) : (
                    <Button type="link" disabled>접근 불가</Button>
                  )}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 분포 위젯 */}
      <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
        <Col xs={24} md={12} lg={8}>
          <Card title="용어: 도메인 분포(Top 5)" extra={<Link to="/terms">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : <MiniBar data={termDomainDist} color="#69c0ff" />}
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card title="단어: 도메인 분포(Top 5)" extra={<Link to="/words">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : <MiniBar data={wordDomainDist} color="#95de64" />}
          </Card>
        </Col>
        <Col xs={24} md={24} lg={8}>
          <Card title="도메인: 데이터유형 분포(Top 5)" extra={<Link to="/domains">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : <MiniBar data={domainTypeDist} color="#ffd666" />}
          </Card>
        </Col>
      </Row>

      {/* 최근 7일 명칭 리스트 */}
      <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="최근 7일: 용어" extra={<Link to="/terms">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : (allow.terms ? <RecentList items={recentTerms} /> : <span style={{ color:'#8c8c8c' }}>권한없음</span>)}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="최근 7일: 단어" extra={<Link to="/words">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : (allow.words ? <RecentList items={recentWords} /> : <span style={{ color:'#8c8c8c' }}>권한없음</span>)}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="최근 7일: 도메인" extra={<Link to="/domains">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : (allow.domains ? <RecentList items={recentDomains} /> : <span style={{ color:'#8c8c8c' }}>권한없음</span>)}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="최근 7일: 프로젝트" extra={<Link to="/projects">더보기</Link>} styles={{ body: { minHeight: 180 } }}>
            {loading || permLoading ? <Skeleton active /> : (allow.projects ? <RecentList items={recentProjects} /> : <span style={{ color:'#8c8c8c' }}>권한없음</span>)}
          </Card>
        </Col>
      </Row>

      {/* 리프레시 버튼 */}
      <div style={{ position: 'fixed', right: 24, bottom: 24 }}>
        <Button type="primary" icon={<ReloadOutlined />} onClick={fetchCounts} loading={loading}>
          새로고침
        </Button>
      </div>
    </div>
  );
}
